/**
 * Backend Currency model ile uyumlu fiyat formatlama.
 * symbol_position, thousands_separator, decimal_separator kullanır.
 * Sadece symbol gösterilir (code ayrıca eklenmez); böylece "EUR €" tekrarı oluşmaz.
 */

export interface CurrencyForFormat {
    code?: string;
    symbol?: string;
    symbol_position?: 'before' | 'after';
    thousands_separator?: string;
    decimal_separator?: string;
}

/**
 * Tek bir tutarı para birimi kurallarına göre formatlar.
 * Örnek: symbol_position 'after', decimal_separator ',' → "1.944,00 €"
 * Örnek: symbol_position 'before' → "€ 1.944,00"
 * Code (EUR) ayrıca yazılmaz; sadece symbol kullanılır.
 */
export function formatCurrencyAmount(
    amount: number,
    currency: CurrencyForFormat | null | undefined
): string {
    if (!currency) {
        return amount.toFixed(2);
    }
    const symbol = (currency.symbol || currency.code || '').trim();
    const position = currency.symbol_position || 'after';
    const thousandsSeparator = currency.thousands_separator ?? ',';
    const decimalSeparator = currency.decimal_separator ?? '.';
    const amountStr = amount.toFixed(2);
    const [integerPart, decimalPart] = amountStr.split('.');
    const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSeparator);
    const formattedAmount = `${formattedInteger}${decimalSeparator}${decimalPart}`;
    if (position === 'before') {
        return symbol ? `${symbol} ${formattedAmount}` : formattedAmount;
    }
    return symbol ? `${formattedAmount} ${symbol}` : formattedAmount;
}

/**
 * Oda bazlı formül metnini üretir: "2× 670,00 + 880,00 = 1.994,00 €"
 * parts: her biri { count, unitPrice } (per person) veya { single } veya { extraBed } veya { child, amount }
 */
export function formatCurrencyBreakdown(
    parts: Array<
        | { count: number; unitPrice: number }
        | { single: number }
        | { extraBed: number }
        | { child: number; amount: number }
    >,
    total: number,
    currency: CurrencyForFormat | null | undefined
): string {
    const fmt = (n: number) => formatCurrencyAmount(n, currency);
    const segs: string[] = [];
    for (const p of parts) {
        if ('count' in p && 'unitPrice' in p) {
            segs.push(`${p.count}× ${fmt(p.unitPrice)}`);
        } else if ('single' in p) {
            segs.push(fmt(p.single));
        } else if ('extraBed' in p) {
            segs.push(fmt(p.extraBed));
        } else if ('child' in p && 'amount' in p) {
            segs.push(fmt(p.amount));
        }
    }
    if (segs.length === 0) return fmt(total);
    return `${segs.join(' + ')} = ${fmt(total)}`;
}
